import { Page, Header, Section, Button, Image, FAQs } from "@/components";
import { tinaClient, useTina, tinaField, ANIMATED_ITEM } from "@/utils";
import axios from "axios";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormSuccess,
  FormGeneralError,
} from "@/ui/form";
import { Textarea } from "@/ui/textarea";
import { Input } from "@/ui/input";
import { motion } from "framer-motion";

export default function ReferPatients({ referPatientsQuery }) {
  const { data } = useTina({
    ...referPatientsQuery,
  });

  const { header, form, faqs, seo } = data.referPatients;

  return (
    <Page
      className="[&>section]:mt-20 lg:[&>section]:mt-32 [&>section:first-of-type]:mt-8 lg:[&>section:first-of-type]:mt-12"
      seo={seo}
    >
      <Header header={header} breadCrumb="Refer a Patient" variant="short-left" />
      <ReferPatientsSection form={form} />
      <FAQs faqs={faqs} />
    </Page>
  );
}

function ReferPatientsSection({ form }) {
  return (
    <Section className="flex flex-col lg:flex-row gap-24 relative" animate={true}>
      <motion.div className="flex-1 relative" variants={ANIMATED_ITEM}>
        <ReferPatientsForm formSuccessDetails={form} />
      </motion.div>
      <motion.div variants={ANIMATED_ITEM} className="hidden lg:block">
        <Image
          src={form?.image}
          alt="Contact Us"
          className="sticky top-4 w-full max-w-[520px] lg:min-h-[720px] h-full lg:max-h-[720px] rounded-md"
          data-tina-field={tinaField(form, "image")}
          width={520}
          height={720}
        />
      </motion.div>
    </Section>
  );
}

const formSchema = z.object({
  name: z.string().min(3, "Invalid Name"),
  dob: z.string().min(3, "Invalid DOB"),
  phone: z.string().min(10, "Invalid Number"),
  message: z.string().min(3, "Invalid Message"),
  // urgent: z.boolean(),
  doctorName: z.string().min(3, "Required"),
  practiceName: z.string().min(3, "Required"),
  practicePhone: z.string().min(10, "Invalid Number"),
  email: z.string().email("Invalid Email"),
});

function ReferPatientsForm({ formSuccessDetails }) {
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      dob: "",
      phone: "",
      message: "",
      // urgent: false,

      doctorName: "",
      practiceName: "",
      practicePhone: "",
      email: "",
    },
  });

  async function onSubmit(values) {
    try {
      await axios.post(`/api/email`, {
        values,
        template: "refer",
      });

      setTimeout(() => {
        form.reset();
      }, 10000);
    } catch (err) {
      form.setError("general", {
        type: "manual",
        message: "An error occurred, please try again or contact us directly.",
      });
      console.log("FAILED...", err);
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="relative space-y-8">
        <div className="bg-secondary text-xl font-title px-4 py-3 rounded -mx-4">Patient Details</div>
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Patient Full Name</FormLabel>
              <FormControl>
                <Input placeholder="John Smith" {...field} />
              </FormControl>

              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="dob"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Patient Date of Birth</FormLabel>
              <FormControl>
                <Input placeholder="DD/MM/YYYY" {...field} />
              </FormControl>

              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Patient Contact Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="7400 123456" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="message"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Presenting Problem</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Please add any patient details or specific request here"
                  className="resize-none"
                  rows={4}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="bg-secondary text-xl font-title px-4 py-3 rounded -mx-4">Referrer Details</div>
        <FormField
          control={form.control}
          name="doctorName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Referrer's Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Dr. Jane Smith" {...field} />
              </FormControl>

              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="practiceName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Practice Name</FormLabel>
              <FormControl>
                <Input placeholder="Practice Name" {...field} />
              </FormControl>

              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="practicePhone"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Practice Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="7400 123456" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Practice Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="your_mail@example.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex flex-col md:flex-row justify-between items-center !mt-12 gap-4">
          <Button
            type="submit"
            className="w-full md:max-w-[280px]"
            disabled={form.formState.isSubmitting}
            aria-label="Submit"
          >
            {form.formState.isSubmitting ? "Submitting..." : "Submit"}
          </Button>
          <FormGeneralError form={form} />
        </div>
      </form>

      {form.formState.isSubmitSuccessful && <FormSuccess formSuccessDetails={formSuccessDetails}></FormSuccess>}
    </Form>
  );
}

export const getStaticProps = async () => {
  const referPatientsQuery = await tinaClient.queries.referPatients({
    relativePath: "refer-patients.mdx",
  });

  return {
    props: {
      referPatientsQuery,
    },
  };
};
